import * as i0 from '@angular/core';
import { EventEmitter, ElementRef, Component, ViewChild, Output, Input, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { coerceNumberProperty, coerceBooleanProperty } from '@roosevelt/common-ui-lib/core';
import { CardHeaderComponent, CardComponent, CardModule } from '@roosevelt/common-ui-lib/card';
import { IconComponent, IconModule } from '@roosevelt/common-ui-lib/icon';
import { CdkDrag, CdkDragHandle, DragDropModule } from '@angular/cdk/drag-drop';

/**
 * Dialog Component
 *
 * @description A dialog component that comes in a few styles and few colors
 * @example
 * <!-- rs-doc {link: 'Dialog with no text in header'} -->
 * <button rs-raised-button (click)="showDialogWithheader=true">Do A Demo</button>
 * <rs-dialog [visible]="showDialogWithheader" heading="Demo" (visibleChange)="showDialogWithheader=false">
 *  <h5 style="display: flex; align-items:center; justify-content: center;">Testing demo content</h5>
 *  <div style="display: flex; align-items:center; justify-content: center; padding: 10px;">
 *   <button class="btn-center" rs-raised-button (click)="showDialogWithheader=false">Close</button>
 *  </div>
 * </rs-dialog>
 * @example
 * <!-- rs-doc {link: 'Dynamic dialog with movable placement and resizable'} -->
 * <button rs-raised-button (click)="d ? d.push(true): d = [true]">Spawn A New Dialog</button>
 * <span *ngIf="d && d.length">
 * <rs-dialog maxWidth="320px" *ngFor="let f of d; let i = index" canMove canResize hideOverlay [origin]="{left: i * 100, top: 400}" [visible]="d[i]" heading="Demo">
 *  <h5 style="display: flex; align-items:center; justify-content: center;">Testing demo content{{i}}</h5>
 *  <div style="display: flex; align-items:center; justify-content: center; padding: 10px;">
 *  <rs-select [items]="[{ value: 'G', label: 'General' }, { value: 'CMS', label: 'Cash Managment Representative' }]"></rs-select>
 *  </div>
 * </rs-dialog>
 * </span>
 */
class DialogComponent {
    constructor() {
        this.headerRef = undefined;
        /** emits when ever the visible state changes in the dialog */
        this.visibleChange = new EventEmitter();
        /** orgin is optional when working with canMove */
        this.origin = undefined;
        this.top = undefined;
        this.left = undefined;
        this.draggable = false;
        this.active = false;
        this._baseZIndex = 3;
        this._hideOverlay = false;
        this._canMove = false;
        this._canResize = false;
        this._visible = false;
        this._heading = '';
        this._maxWidth = '';
    }
    /** an index that is applied to the set(overlay, dialogs and active dialog) */
    get baseZIndex() {
        return this._baseZIndex;
    }
    set baseZIndex(value) {
        this._baseZIndex = coerceNumberProperty(value);
    }
    /** hides the overlay allowing you to interact with the page behind it */
    get hideOverlay() {
        return this._hideOverlay;
    }
    set hideOverlay(value) {
        this._hideOverlay = coerceBooleanProperty(value);
    }
    /** can drag the dialog around the screen */
    get canMove() {
        return this._canMove;
    }
    set canMove(value) {
        this._canMove = coerceBooleanProperty(value);
    }
    /** can you resize the dialog */
    get canResize() {
        return this._canResize;
    }
    set canResize(value) {
        this._canResize = coerceBooleanProperty(value);
    }
    /** to hide or show the dialog */
    get visible() {
        return this._visible;
    }
    set visible(value) {
        this._visible = coerceBooleanProperty(value);
        setTimeout(() => {
            if (this._visible && this.origin) {
                this.left = this.origin.x || this.origin.left;
                this.top = this.origin.y || this.origin.top;
            }
        }, 0);
    }
    /** top title on the dialog */
    get heading() {
        return this._heading;
    }
    set heading(value) {
        this._heading = value;
    }
    /** forces the max width to anything assigned */
    get maxWidth() {
        return this._maxWidth;
    }
    set maxWidth(value) {
        this._maxWidth = value;
    }
    close() {
        this.visible = false;
        this.visibleChange.emit(this.visible);
    }
    /* eslint-disable */
    static { this.ngAcceptInputType_baseZIndex = undefined; }
    static { this.ngAcceptInputType_visible = undefined; }
    static { this.ngAcceptInputType_canMove = undefined; }
    static { this.ngAcceptInputType_canResize = undefined; }
    static { this.ngAcceptInputType_hideOverlay = undefined; }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DialogComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.13", type: DialogComponent, isStandalone: true, selector: "rs-dialog", inputs: { origin: "origin", baseZIndex: "baseZIndex", hideOverlay: "hideOverlay", canMove: "canMove", canResize: "canResize", visible: "visible", heading: "heading", maxWidth: "maxWidth" }, outputs: { visibleChange: "visibleChange" }, viewQueries: [{ propertyName: "headerRef", first: true, predicate: CardHeaderComponent, descendants: true, read: ElementRef }], ngImport: i0, template: "@if (visible) {\n  <rs-card tabindex=\"-1\" [style.z-index]=\"!active ? baseZIndex + 1 : baseZIndex + 2\" (cdkDragStarted)=\"draggable = active = true\" (cdkDragEnded)=\"draggable = active = false\" [cdkDragDisabled]=\"!canMove\" cdkDrag class=\"rs-dialog\" [class.active]=\"active\" [class.locked]=\"!canMove\" [class.resizable]=\"canResize\" [fullContent]=\"true\" [style.maxWidth]=\"maxWidth\" [style.top.px]=\"top\" [style.left.px]=\"left\">\n    <rs-card-header cdkDragHandle [class.draggable]=\"canMove\">\n      <div class=\"rs-dialog-heading\">\n        @if (heading) {\n          <span>{{heading}}</span>\n        }\n        <span style=\"flex: 1;\"></span>\n        <rs-icon (click)=\"close()\" style=\"cursor: pointer;\">close</rs-icon>\n      </div>\n    </rs-card-header>\n    <ng-content></ng-content>\n  </rs-card>\n  @if (!hideOverlay) {\n    <div class=\"overlay\" [style.z-index]=\"baseZIndex\"></div>\n  }\n}\n", styles: [":host ::ng-deep .rs-dialog.resizable>.rs-card{overflow:auto;resize:both}.overlay{position:fixed;inset:0;background-color:#000000bf;z-index:3}.rs-dialog{z-index:4;position:fixed;display:block;right:0;left:0;top:20px;min-height:200px;width:auto;outline:0;max-height:95%;max-width:740px}.rs-dialog.locked{margin-right:auto;margin-left:auto}.rs-dialog.active{z-index:5}.rs-dialog rs-card-header.draggable{cursor:move}.rs-dialog .rs-dialog-heading{width:100%;display:flex;align-items:center}@media (min-width: 768px){.rs-dialog{top:40px}}.rs-dialog-close-btn{border:0;background:none;color:#fff;position:absolute;top:15px;right:8px;font-size:1.5em}\n"], dependencies: [{ kind: "component", type: CardComponent, selector: "rs-card", inputs: ["fullContainer", "fullContent"] }, { kind: "directive", type: CdkDrag, selector: "[cdkDrag]", inputs: ["cdkDragData", "cdkDragLockAxis", "cdkDragRootElement", "cdkDragBoundary", "cdkDragStartDelay", "cdkDragFreeDragPosition", "cdkDragDisabled", "cdkDragConstrainPosition", "cdkDragPreviewClass", "cdkDragPreviewContainer", "cdkDragScale"], outputs: ["cdkDragStarted", "cdkDragReleased", "cdkDragEnded", "cdkDragEntered", "cdkDragExited", "cdkDragDropped", "cdkDragMoved"], exportAs: ["cdkDrag"] }, { kind: "component", type: CardHeaderComponent, selector: "rs-card-header", inputs: ["expandable", "isExpanded", "color"], outputs: ["expanded"] }, { kind: "directive", type: CdkDragHandle, selector: "[cdkDragHandle]", inputs: ["cdkDragHandleDisabled"] }, { kind: "component", type: IconComponent, selector: "rs-icon" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DialogComponent, decorators: [{
            type: Component,
            args: [{ selector: 'rs-dialog', standalone: true, imports: [CardComponent, CdkDrag, CardHeaderComponent, CdkDragHandle, IconComponent], template: "@if (visible) {\n  <rs-card tabindex=\"-1\" [style.z-index]=\"!active ? baseZIndex + 1 : baseZIndex + 2\" (cdkDragStarted)=\"draggable = active = true\" (cdkDragEnded)=\"draggable = active = false\" [cdkDragDisabled]=\"!canMove\" cdkDrag class=\"rs-dialog\" [class.active]=\"active\" [class.locked]=\"!canMove\" [class.resizable]=\"canResize\" [fullContent]=\"true\" [style.maxWidth]=\"maxWidth\" [style.top.px]=\"top\" [style.left.px]=\"left\">\n    <rs-card-header cdkDragHandle [class.draggable]=\"canMove\">\n      <div class=\"rs-dialog-heading\">\n        @if (heading) {\n          <span>{{heading}}</span>\n        }\n        <span style=\"flex: 1;\"></span>\n        <rs-icon (click)=\"close()\" style=\"cursor: pointer;\">close</rs-icon>\n      </div>\n    </rs-card-header>\n    <ng-content></ng-content>\n  </rs-card>\n  @if (!hideOverlay) {\n    <div class=\"overlay\" [style.z-index]=\"baseZIndex\"></div>\n  }\n}\n", styles: [":host ::ng-deep .rs-dialog.resizable>.rs-card{overflow:auto;resize:both}.overlay{position:fixed;inset:0;background-color:#000000bf;z-index:3}.rs-dialog{z-index:4;position:fixed;display:block;right:0;left:0;top:20px;min-height:200px;width:auto;outline:0;max-height:95%;max-width:740px}.rs-dialog.locked{margin-right:auto;margin-left:auto}.rs-dialog.active{z-index:5}.rs-dialog rs-card-header.draggable{cursor:move}.rs-dialog .rs-dialog-heading{width:100%;display:flex;align-items:center}@media (min-width: 768px){.rs-dialog{top:40px}}.rs-dialog-close-btn{border:0;background:none;color:#fff;position:absolute;top:15px;right:8px;font-size:1.5em}\n"] }]
        }], propDecorators: { headerRef: [{
                type: ViewChild,
                args: [CardHeaderComponent, { read: ElementRef }]
            }], visibleChange: [{
                type: Output
            }], origin: [{
                type: Input
            }], baseZIndex: [{
                type: Input
            }], hideOverlay: [{
                type: Input
            }], canMove: [{
                type: Input
            }], canResize: [{
                type: Input
            }], visible: [{
                type: Input
            }], heading: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }] } });

class DialogModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DialogModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: DialogModule, imports: [CommonModule,
            CardModule,
            DragDropModule,
            IconModule,
            DialogComponent], exports: [DialogComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DialogModule, imports: [CommonModule,
            CardModule,
            DragDropModule,
            IconModule,
            DialogComponent] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DialogModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        CardModule,
                        DragDropModule,
                        IconModule,
                        DialogComponent
                    ],
                    exports: [DialogComponent]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { DialogComponent, DialogModule };
